import React, { useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from './firebase';
import { Layout } from "./components/dashboard/layout";
import { Box, TextField, Typography, Button } from '@mui/material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const ReportsFull = () => {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [reportLogs, setReportLogs] = useState([]);
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchData = async () => {
    try {
      const logsSnapshot = await getDocs(collection(db, 'logs'));
      const usersSnapshot = await getDocs(collection(db, 'users'));

      const usersData = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const logsData = logsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      setUsers(usersData);
      setLogs(logsData);
    } catch (error) {
      console.error("Error fetching data: ", error);
    }
  };

  const handleSearch = async () => {
    setIsLoading(true);
  
    if (logs.length === 0 || users.length === 0) {
      await fetchData();
    }
  
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
  
    const formattedFromDate = formatDate(fromDate);
    const formattedToDate = formatDate(toDate);
  
    const groupedLogs = new Map();
  
    logs.forEach(log => {
      const logDate = log.datetime && typeof log.datetime === 'string' ? log.datetime.split(' ')[0] : null;
  
      if (logDate && logDate >= formattedFromDate && logDate <= formattedToDate) {
        const user = users.find(user => 
          String(user.userId) === String(log.id)
        );
  
        if (user) {
          const key = `${user.userId}-${logDate}`;
          if (!groupedLogs.has(key)) {
            groupedLogs.set(key, {
              id: log.id,
              userId: user.userId,
              name: user.name,
              grade: user.grade_level,
              date: logDate,
              logTime: log.datetime ? log.datetime.split(' ')[1] : '', 
              mode: log.mode,
              present: true,
            });
          }
        }
      }
    });
  
    // Convert the map to an array and sort by logTime in descending order
    const sortedLogs = [...groupedLogs.values()].sort((a, b) => {
      return b.logTime.localeCompare(a.logTime); // Sort by time in descending order
    });
  
    setReportLogs(sortedLogs);
    setIsLoading(false);
  };
  

  // Handle search within the table
  const handleSearchQuery = (event) => {
    setSearchQuery(event.target.value.toLowerCase());
  };

  // Filter logs based on the search query
  // Filter logs based on the search query
const filteredLogs = reportLogs.filter(log =>
  (log.name && log.name.toLowerCase().includes(searchQuery)) ||
  (log.userId && String(log.userId).toLowerCase().includes(searchQuery)) || // Convert userId to string
  (log.grade && log.grade.toString().includes(searchQuery)) // Convert grade to string if it's a number
);


  return (
    <Layout>
      <Box sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>All Reports</Typography>
        
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <DatePicker
            selected={fromDate}
            onChange={(date) => setFromDate(date)}
            dateFormat="yyyy-MM-dd"
            customInput={<TextField variant="outlined" label="From Date" />}
          />
          <DatePicker
            selected={toDate}
            onChange={(date) => setToDate(date)}
            dateFormat="yyyy-MM-dd"
            customInput={<TextField variant="outlined" label="To Date" />}
          />
          <Button variant="contained" color="secondary" onClick={handleSearch} disabled={isLoading}>
            {isLoading ? 'Loading...' : "Search"}
          </Button>
        </Box>

        {/* Search bar for filtering */}
        <TextField
          variant="outlined"
          label="Search"
          value={searchQuery}
          onChange={handleSearchQuery}
          fullWidth
          sx={{ mb: 2 }}
        />

        {/* Always show the plain table */}
        <Box sx={{ mt: 3 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ borderBottom: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>User ID</th>
                <th style={{ borderBottom: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Name</th>
                <th style={{ borderBottom: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Grade</th>
                <th style={{ borderBottom: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Mode</th>
                <th style={{ borderBottom: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Status</th>
                <th style={{ borderBottom: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Date</th>
                <th style={{ borderBottom: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Log Time</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map((log, index) => (
                <tr key={index}>
                  <td style={{ borderBottom: '1px solid #ddd', padding: '8px' }}>{log.userId}</td>
                  <td style={{ borderBottom: '1px solid #ddd', padding: '8px' }}>{log.name}</td>
                  <td style={{ borderBottom: '1px solid #ddd', padding: '8px' }}>{log.grade}</td>
                  <td style={{ borderBottom: '1px solid #ddd', padding: '8px' }}>{log.mode}</td>
                  <td style={{ borderBottom: '1px solid #ddd', padding: '8px', color: log.present ? 'green' : 'red' }}>
                    {log.present ? 'Present' : 'Absent'}
                  </td>
                  <td style={{ borderBottom: '1px solid #ddd', padding: '8px' }}>{log.date}</td>
                  <td style={{ borderBottom: '1px solid #ddd', padding: '8px' }}>{log.logTime}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </Box>
    </Layout>
  );
};

export default ReportsFull;
